/**
 * src/lib/hooks/useAtMentions.js
 *
 * Phase 82-04 — Chat System Unification.
 *
 * Detects "@..." inside the composer and returns a ranked list of entities
 * the user can reference. Selection inserts a "@<Entity Name>" token into
 * the input AND emits a scope entry so the chat gains a chip.
 *
 * Entity sources (AppDataContext):
 *   projects | crm (deals) | tasks | people
 *
 * No port tab — scopeContract forbids port entries.
 *
 * Hooks at top of body — Phase 90-07 rule.
 */

import { useState, useMemo, useEffect, useCallback } from 'react';
import { useAppDataContext } from '../AppDataContext';
import { toScopeEntry } from '../agent/scopeContract';
import { searchScope } from '../agent/scopeSearch';

const RESULT_CAP = 40;
const DEBOUNCE_MS = 100;

function parseAtTrigger(text, caret) {
  if (typeof text !== 'string') return null;
  const cursor = Number.isInteger(caret) ? caret : text.length;
  const before = text.slice(0, cursor);
  const atIdx = Math.max(before.lastIndexOf('@'), -1);
  if (atIdx < 0) return null;
  const prevChar = atIdx === 0 ? '' : before[atIdx - 1];
  if (prevChar && !/\s/.test(prevChar)) return null;
  const token = before.slice(atIdx + 1);
  if (/\s/.test(token)) return null;
  return { start: atIdx, end: cursor, query: token };
}

export function useAtMentions({ text, caret, enabled = true } = {}) {
  const appCtx = useAppDataContext();
  const appData = useMemo(() => appCtx || {}, [appCtx]);
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [cursor, setCursor] = useState(0);

  const trigger = enabled ? parseAtTrigger(text, caret) : null;
  const open = trigger !== null;
  const query = trigger?.query ?? '';

  useEffect(() => {
    const id = setTimeout(() => setDebouncedQuery(query), DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [query]);

  useEffect(() => {
    setCursor(0);
  }, [debouncedQuery, open]);

  // TICKET-1148: the same smart search as the scope picker (accent-free, every
  // word in any order, name over context, live over closed; leads named by
  // `opportunity`, which the old local matcher missed so leads never listed).
  const results = useMemo(() => {
    if (!open) return [];
    return searchScope(appData, ['project', 'deal', 'task', 'person'], debouncedQuery, { cap: RESULT_CAP });
  }, [open, debouncedQuery, appData]);

  useEffect(() => {
    if (cursor >= results.length) setCursor(Math.max(0, results.length - 1));
  }, [cursor, results.length]);

  const move = useCallback((delta) => {
    setCursor((prev) => {
      const n = results.length;
      if (n === 0) return 0;
      return (prev + delta + n) % n;
    });
  }, [results.length]);

  const getHighlighted = useCallback(() => results[cursor] || null, [results, cursor]);

  const buildInsertion = useCallback((choice) => {
    if (!choice || !trigger) return null;
    let entry;
    try {
      entry = toScopeEntry(choice.row, choice.type);
    } catch {
      return null;
    }
    const mentionToken = `@${choice.label}`;
    const safeText = typeof text === 'string' ? text : '';
    const next = safeText.slice(0, trigger.start) + mentionToken + ' ' + safeText.slice(trigger.end);
    const nextCaret = trigger.start + mentionToken.length + 1;
    return { text: next, caret: nextCaret, entry };
  }, [text, trigger]);

  const onKeyDown = useCallback((e, onSelect, onClose) => {
    if (!open) return false;
    if (e.key === 'ArrowDown') { e.preventDefault(); move(1); return true; }
    if (e.key === 'ArrowUp')   { e.preventDefault(); move(-1); return true; }
    if (e.key === 'Enter' || e.key === 'Tab') {
      const choice = getHighlighted();
      if (choice) {
        const payload = buildInsertion(choice);
        if (payload) {
          e.preventDefault();
          onSelect?.(payload);
          return true;
        }
      }
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose?.();
      return true;
    }
    return false;
  }, [open, move, getHighlighted, buildInsertion]);

  return {
    open,
    query,
    results,
    cursor,
    setCursor,
    move,
    getHighlighted,
    buildInsertion,
    onKeyDown,
    trigger,
  };
}

export default useAtMentions;
